import { Search, X, Filter, Download, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectOption } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';

interface FilterConfig {
  type: 'search' | 'select' | 'date';
  key: string;
  placeholder?: string;
  options?: SelectOption[];
  className?: string;
}

interface FilterBarProps {
  filters: FilterConfig[];
  values: Record<string, string | Date | undefined>;
  onChange: (key: string, value: string | Date | undefined) => void;
  onReset?: () => void;
  onExport?: () => void;
  onRefresh?: () => void;
  isExporting?: boolean;
  isRefreshing?: boolean;
  className?: string;
}

export function FilterBar({
  filters,
  values,
  onChange,
  onReset,
  onExport,
  onRefresh,
  isExporting = false,
  isRefreshing = false,
  className,
}: FilterBarProps) {
  const hasActiveFilters = Object.values(values).some(
    (v) => v !== undefined && v !== '' && v !== 'all'
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {filters.map((filter) => {
        switch (filter.type) {
          case 'search':
            return (
              <div key={filter.key} className={cn('relative', filter.className || 'w-64')}>
                <Input
                  placeholder={filter.placeholder || 'Search...'}
                  value={(values[filter.key] as string) || ''}
                  onChange={(e) => onChange(filter.key, e.target.value)}
                  leftIcon={<Search className="h-4 w-4" />}
                />
              </div>
            );

          case 'select':
            return (
              <Select
                key={filter.key}
                value={(values[filter.key] as string) || ''}
                onChange={(value) => onChange(filter.key, value)}
                options={filter.options || []}
                placeholder={filter.placeholder}
                className={filter.className || 'w-40'}
              />
            );

          case 'date':
            return (
              <DatePicker
                key={filter.key}
                value={values[filter.key] as Date | undefined}
                onChange={(date) => onChange(filter.key, date)}
                placeholder={filter.placeholder}
                className={filter.className || 'w-40'}
              />
            );

          default:
            return null;
        }
      })}

      <div className="flex items-center gap-2 ml-auto">
        {hasActiveFilters && onReset && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}

        {onRefresh && (
          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
        )}

        {onExport && (
          <Button
            variant="outline"
            onClick={onExport}
            disabled={isExporting}
            isLoading={isExporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        )}
      </div>
    </div>
  );
}

// Quick filter presets
interface QuickFilter {
  label: string;
  value: Record<string, string>;
}

interface QuickFiltersProps {
  filters: QuickFilter[];
  activeFilter?: string;
  onSelect: (filter: QuickFilter) => void;
  className?: string;
}

export function QuickFilters({
  filters,
  activeFilter,
  onSelect,
  className,
}: QuickFiltersProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Filter className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Quick filters:</span>
      {filters.map((filter) => (
        <Button
          key={filter.label}
          variant={activeFilter === filter.label ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelect(filter)}
        >
          {filter.label}
        </Button>
      ))}
    </div>
  );
}
